/* eslint-disable react/prop-types, max-len */
import {
  ellipsis, H3, Link, List as DefaultList, nowrap,
  ListItem as DefaultListItem
} from '@codex/basics';
import { replacePlaceholders, resolveByCountry } from '@codex/helpers';
import React from 'react';
import styled, { css } from 'styled-components';
import {
  LAYOUT_1_0,
  LAYOUT_1_2, LAYOUT_2_0, LAYOUT_2_2
} from '../../constants';
import ProductImage from '../../ProductImage/index';
import StatusLabel from '../../StatusLabel/index';

const MY_ACCOUNT_PATH = resolveByCountry(
  '/gbuk/s/my-account.html',
  '/ieen/s/my-account.html'
);

const List = styled(DefaultList)`
  margin-top: ${({ theme, layout }) => (
    [LAYOUT_1_2, LAYOUT_2_0].includes(layout) ? theme.spacing[3] : 0
  )};
  display: flex;
  flex-direction: ${({ layout }) => ([LAYOUT_1_2, LAYOUT_2_0].includes(layout) ? 'row' : 'column')};
  flex-wrap: wrap;
`;

const ListItem = styled(DefaultListItem)`
  ${({ layout }) => ([LAYOUT_1_2, LAYOUT_2_0].includes(layout) &&
  css`
      margin-right: ${({ theme }) => theme.spacing[3]};
    `
  )};
  
  &:last-of-type {
    margin-right: 0;
  }
  
  margin-bottom: ${({ theme }) => theme.spacing[1]};
  white-space: nowrap;
`;

const listRenderer = layout => (item, index) => (
  <ListItem key={index} layout={layout}>
    <Link href={item.url} {...(item.attrs || {})}>{item.label}</Link>
  </ListItem>
);

const Container = styled.div`
  padding: ${({ theme }) => theme.spacing[4]};
  margin-bottom: 0;
  
  @media ${({ theme }) => theme.breakpoints.tight} {
    padding: ${({ theme }) => theme.spacing[2]};
  }
  
  padding-top: ${({ theme }) => theme.spacing[3]};
  
  > ${H3} {
    display: flex;
    justify-content: space-between;
    line-height: 1.1;
    font-weight: ${({ theme }) => theme.fontWeights.normal};
    margin-bottom: ${({ theme }) => theme.spacing[3]};
    
    @media ${({ theme }) => theme.breakpoints.tight} {
      margin-bottom: ${({ theme }) => theme.spacing[2]};
    }
  }
  
  ${props => props.appearance === 'box' && css`
    border: 1px solid ${props.theme.colors.shades[5]};
    background-color: ${props.theme.colors.shades[7]};
  `};
`;

const Content = styled.div`
  display: flex;
`;

const Links = styled.div`
  display: flex;
  flex-direction: column;
  padding-left: 30px;
  
  @media ${({ theme }) => theme.breakpoints.tight} {
    padding-left: ${({ theme }) => theme.spacing[4]};
  }
  
  margin-top: ${({ theme }) => theme.spacing[1]};
  overflow: hidden;
`;

const ImageContainer = styled.div`
  display: flex;
  flex-direction: column;
  
  ${ProductImage} {
    margin-left: ${({ theme, layout }) => ([LAYOUT_1_2, LAYOUT_2_2].includes(layout) ? theme.spacing[1] : 0)};
    
    @media ${({ theme }) => theme.breakpoints.tight} {
      margin-left: 0;
    }
  }
`;

const ProductLink = styled(Link)`
  display: block;
  line-height: 1.1;
  ${ellipsis()};
  ${nowrap()};
`;

const NoteWrapper = styled.span`
  line-height: 1.1;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.shades[1]};
  font-style: italic;
  margin-top: ${({ theme, layout }) => ([LAYOUT_2_0, LAYOUT_1_0].includes(layout) ? theme.spacing[1] : theme.spacing[2])};
  
  ${({ layout }) => [LAYOUT_2_2].includes(layout) && css`
    margin-right: ${({ theme }) => `-${theme.spacing[2]}`};
    margin-left: ${({ theme }) => `-${theme.spacing[1]}`};
    text-align: center;
    width: 100px;
    
    @media ${({ theme }) => theme.breakpoints.tight} {
      width: 60px;
      margin-right: 0;
      margin-left: 0;
    }
  `};
`;

const Date = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.shades[1]};
  white-space: nowrap;
`;

const Note = props => (
  <NoteWrapper layout={props.layout}>
    {replacePlaceholders(
      props.plusItemsMessage,
      {
        '[NUMBER_OF_ITEMS]': props.numberOfProducts - 1,
        '[S]': props.numberOfProducts > 2 ? 's' : ''
      }
    )}
  </NoteWrapper>
);

const GuestLinkWrapper = styled.span`
  margin-top: ${({ theme, layout }) => (
    [LAYOUT_1_2, LAYOUT_2_0].includes(layout) ? theme.spacing[3] : 0
  )};
  
  strong {
    font-weight: ${({ theme }) => theme.fontWeights.bold};
  }
`;

const Title = props => (props.guest
  ? <span>{props.children}</span>
  : <Link {...props} />
);

const SplitScreenItem = props => (
  <Container appearance={props.appearance} className={props.className} layout={props.layout}>
    <H3>
      <Title
        guest={props.guest}
        href={`${MY_ACCOUNT_PATH}#order-detail/${props.orderReference}`}
        data-qa="order_number"
        data-interaction="Orders module: order number"
      >
        {replacePlaceholders(
          props.orderTitle,
          {
            '[ORDER_REFERENCE]': props.orderReference
          }
        )}
      </Title>
      {[LAYOUT_1_2, LAYOUT_2_0].includes(props.layout) && (
        <Date data-qa="order_date">{props.orderDate}</Date>
      )}
    </H3>
    <Content>
      <ImageContainer layout={props.layout}>
        <Link href={props.mainProduct.productUrl}>
          <ProductImage
            src={props.mainProduct.image}
            alt={props.mainProduct.name}
            layout={props.layout}
            data-interaction="Orders module: product image"
          />
        </Link>
        {props.layout === LAYOUT_2_2 && props.numberOfProducts > 1 && <Note {...props} />}
      </ImageContainer>
      <Links>
        <StatusLabel dataQa="order_status" status={props.orderStatus} />
        {[LAYOUT_1_2, LAYOUT_2_0].includes(props.layout) && (
          <>
            <ProductLink
              data-qa="product_name"
              href={props.mainProduct.productUrl}
            >
              {props.mainProduct.name}
            </ProductLink>
            {props.numberOfProducts > 1 && <Note {...props} />}
          </>
        )}
        {!props.guest
          ? <List items={props.links} layout={props.layout}>{listRenderer(props.layout)}</List>
          : (
            <GuestLinkWrapper layout={props.layout}>
              <Link href="#" onClick={props.onRegisterAccountLinkClicked}>{props.registerAccountMessageStart}</Link>{' '}
              <span dangerouslySetInnerHTML={{ __html: props.registerAccountMessageEnd }} />
            </GuestLinkWrapper>
          )}
      </Links>
    </Content>
  </Container>
);

SplitScreenItem.displayName = 'RecentOrder-SplitScreen';

export default SplitScreenItem;
